import React, { useEffect, useRef, useState } from "react";
import { ShieldAlert, MapPin, Check, Users, HeartPulse } from "lucide-react";
import Layout from "../components/Layout";
import SOSButton, { ACTIVE_SOS_KEY } from "../components/SOSButton";
import MarkSafeFlow from "../components/MarkSafeFlow";
import { api, auth } from "../lib/api";
import { useAgencyNavigate } from "../lib/agency";
import { useSettings } from "../lib/settings";
import {
  getNotificationPermission,
  requestNotificationPermission,
  showLocalNotification,
  registerServiceWorkerIfWeb,
} from "../lib/native";

const sameName = (a, b) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

export default function SOSPage() {
  const navigate = useAgencyNavigate();
  const { settings } = useSettings();
  const user = auth.current();
  const savedGroup = JSON.parse(localStorage.getItem("ts_my_group") || "null");
  const group = savedGroup?.group;
  // myName: quién es ESTE dispositivo — el viajero logueado, o el nombre que
  // eligió al crear/unirse al grupo como guest (savedGroup.myName). Antes se
  // usaba group?.creator_name como comodín para cualquier guest, así que
  // todos los miembros de un grupo terminaban viéndose a sí mismos como "el
  // que mandó la alerta", sin importar quién la mandó de verdad.
  const myName = user?.traveler_name || savedGroup?.myName || group?.creator_name || "Invitado";
  const [lastAlert, setLastAlert] = useState(null);
  const [activeAlertId, setActiveAlertId] = useState(null);
  const [checkingActive, setCheckingActive] = useState(true);
  const [justResolved, setJustResolved] = useState(false);
  const [permission, setPermission] = useState("default");
  const [persistentShown, setPersistentShown] = useState(false);

  const isMine = !!lastAlert && sameName(lastAlert.sender_name, myName);

  const permissionRef = useRef(permission);
  const activeAlertIdRef = useRef(activeAlertId);
  const agencyNameRef = useRef(settings.agency_name);
  useEffect(() => { permissionRef.current = permission; }, [permission]);
  useEffect(() => { activeAlertIdRef.current = activeAlertId; }, [activeAlertId]);
  useEffect(() => { agencyNameRef.current = settings.agency_name; }, [settings.agency_name]);

  useEffect(() => {
    registerServiceWorkerIfWeb();
    getNotificationPermission().then(setPermission);
  }, []);

  const checkActive = () => {
    const saved = JSON.parse(localStorage.getItem(ACTIVE_SOS_KEY) || "null");
    const bId = user?.booking_id || saved?.booking_id;
    const gId = group?.id || saved?.group_id;
    if (!bId && !gId) return Promise.resolve();
    return api
      .get("/sos/mine", { params: { booking_id: bId, group_id: gId } })
      .then(({ data }) => {
        if (data.alert) {
          setLastAlert(data.alert);
          setActiveAlertId(data.alert.id);
        } else {
          localStorage.removeItem(ACTIVE_SOS_KEY);
          setLastAlert(null);
          setActiveAlertId(null);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    // Recupera si sigue habiendo una alerta activa después de cerrar/reabrir
    // la app — antes este estado vivía solo en memoria de React y se perdía
    // por completo. Se confirma con el backend (no solo localStorage) para
    // que también se "autocorrija" si la agencia ya la resolvió mientras la
    // app estaba cerrada.
    setCheckingActive(true);
    checkActive().finally(() => setCheckingActive(false));
    // Antes dependía de [] (solo el primer render): si el login o la carga
    // del grupo ocurrían DESPUÉS de montar (sin recargar la página completa),
    // este efecto nunca se volvía a correr con el user/group reales y la
    // reconciliación con el backend simplemente no pasaba.
  }, [user?.booking_id, group?.id]);

  useEffect(() => {
    // Actualización en vivo: antes, si alguien más del grupo mandaba un SOS
    // (o lo resolvía) mientras vos tenías la pantalla abierta, no te
    // enterabas hasta recargar a mano. Sondeo cada 15s + al volver a esta
    // pestaña (visibilitychange) cubre el caso sin necesitar sockets.
    const interval = setInterval(checkActive, 15000);
    const onVisible = () => { if (document.visibilityState === "visible") checkActive(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.booking_id, group?.id]);

  useEffect(() => {
    // La notificación persistente "recupera" su lugar aunque el sistema
    // operativo la haya limpiado — se vuelve a publicar con el mismo tag
    // cada vez que confirmamos que la alerta sigue activa. Solo para la
    // propia: si es de otro miembro, no tiene sentido pedirte a VOS que te
    // marques a salvo.
    if (activeAlertId && isMine && permission === "granted") {
      showLocalNotification(
        "🆘 Tu alerta SOS sigue activa",
        "Toca para ver el estado o marcarte como seguro.",
        { tag: "cherry-sos-active", url: "/sos", actions: [{ action: "view-sos", title: "Ver estado" }] }
      );
    }
  }, [activeAlertId, isMine, permission]);

  useEffect(() => {
    // El recordatorio genérico de "SOS disponible" solo tiene sentido si no
    // hay ya una alerta activa (si no, sería confuso mostrar los dos).
    if (permission === "granted" && !persistentShown && !checkingActive && !activeAlertId) {
      showLocalNotification(
        `${settings.agency_name} · SOS disponible`,
        "Toca para acceder al botón SOS de emergencia.",
        { tag: "cherry-sos-shortcut", url: "/sos" }
      );
      setPersistentShown(true);
    }
  }, [permission, persistentShown, checkingActive, activeAlertId, settings.agency_name]);

  useEffect(() => {
    // Vuelve a dejar el recordatorio "SOS disponible" al SALIR de esta
    // pantalla — la idea es que el atajo siempre esté a un toque de
    // distancia, no solo la primera vez que se abrió la app en la sesión.
    return () => {
      if (permissionRef.current === "granted" && !activeAlertIdRef.current) {
        showLocalNotification(
          `${agencyNameRef.current || "Travel Shield"} · SOS disponible`,
          "Toca para acceder al botón SOS de emergencia.",
          { tag: "cherry-sos-shortcut", url: "/sos" }
        );
      }
    };
  }, []);

  const requirePush = !user && (!group || permission !== "granted");

  const askPermission = async () => {
    const p = await requestNotificationPermission();
    setPermission(p);
  };

  const onMarkedSafe = async () => {
    localStorage.removeItem(ACTIVE_SOS_KEY);
    setActiveAlertId(null);
    setLastAlert(null);
    setJustResolved(true);
    if ("serviceWorker" in navigator) {
      try {
        const reg = await navigator.serviceWorker.ready;
        const notifs = await reg.getNotifications({ tag: "cherry-sos-active" });
        notifs.forEach((n) => n.close());
      } catch {}
    }
  };

  if (requirePush) {
    return (
      <Layout>
        <section className="max-w-2xl mx-auto px-6 py-20 text-center">
          <ShieldAlert className="w-12 h-12 text-[#FF3B30] mx-auto mb-6" strokeWidth={1.4} />
          <h1 className="font-display text-4xl font-light mb-4">SOS bloqueado</h1>
          <p className="text-neutral-400 mb-8">
            Para usar el botón SOS necesitas iniciar sesión como viajero, o crear un Grupo de Emergencia y permitir notificaciones push.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => navigate("/login")}
              data-testid="sos-login-btn"
              className="bg-[var(--brand-cyan,#FF85AC)] text-black px-7 py-3 rounded-full font-semibold hover:bg-[#FF9DC0]"
            >
              Soy Viajero
            </button>
            <button
              onClick={() => navigate("/groups")}
              data-testid="sos-create-group-btn"
              className="border border-white/15 hover:border-[var(--brand-cyan,#FF85AC)] px-7 py-3 rounded-full"
            >
              Crear Grupo
            </button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-12 text-center">
        {activeAlertId && !isMine && !justResolved && (
          <div
            className="glass rounded-3xl p-6 border border-[#FF3B30]/60 text-left mb-10 fade-up"
            data-testid="incoming-alert-banner"
          >
            <div className="flex items-center gap-2 mb-3">
              <HeartPulse className="w-4 h-4 text-[#FF3B30]" />
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">
                Alguien de tu grupo necesita ayuda
              </p>
            </div>
            <p className="text-sm text-white mb-2">
              <b>{lastAlert.sender_name}</b> activó una alerta de emergencia.
            </p>
            {lastAlert.latitude != null && lastAlert.longitude != null ? (
              <a
                href={lastAlert.maps_url}
                target="_blank"
                rel="noreferrer"
                className="text-[var(--brand-cyan,#FF85AC)] text-sm hover:underline break-all"
                data-testid="incoming-maps-link"
              >
                Ver su ubicación · {lastAlert.maps_url}
              </a>
            ) : (
              <p className="text-sm text-neutral-400">
                No se pudo capturar su ubicación GPS — intentá contactarlo por teléfono.
              </p>
            )}
          </div>
        )}

        <p className="text-xs uppercase tracking-[0.4em] text-[#FF3B30] mb-4">Panel de Emergencia</p>
        <h1 className="font-display text-5xl font-light tracking-tight mb-3">
          Botón <span className="text-[#FF3B30]">SOS</span>
        </h1>
        <p className="text-neutral-400 mb-2">
          Mantén presionado 3 segundos para compartir tu ubicación con tu grupo de emergencia.
        </p>
        <p className="text-xs text-neutral-600 mb-10">
          Esto no sustituye una llamada al 911 ni garantiza una respuesta inmediata — úsalo junto con la llamada, no en su lugar.
        </p>

        <div className="my-12">
          <SOSButton
            groupId={group?.id}
            bookingId={user?.booking_id}
            senderName={myName}
            onAlert={(a) => { setLastAlert(a); setActiveAlertId(a.id); }}
            initiallyActive={!!activeAlertId && isMine}
          />
        </div>

        {justResolved && (
          <div className="glass rounded-3xl p-6 border border-[var(--brand-cyan,#FF85AC)]/40 text-center max-w-md mx-auto fade-up" data-testid="resolved-summary">
            <Check className="w-6 h-6 text-[var(--brand-cyan,#FF85AC)] mx-auto mb-2" />
            <p className="text-sm text-white">Confirmado — tu grupo fue avisado que ya estás a salvo.</p>
          </div>
        )}

        {lastAlert && !justResolved && isMine && (
          <div className="glass rounded-3xl p-6 border border-[#FF3B30]/40 text-left max-w-md mx-auto fade-up" data-testid="alert-summary">
            <div className="flex items-center gap-2 mb-3">
              <Check className="w-4 h-4 text-[var(--brand-cyan,#FF85AC)]" />
              <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)]">Alerta enviada a tu grupo</p>
            </div>
            {lastAlert.latitude != null && lastAlert.longitude != null ? (
              <>
                <p className="text-sm text-neutral-300 mb-2">
                  <MapPin className="inline w-3.5 h-3.5 text-[#FF3B30] mr-1" />
                  {lastAlert.latitude.toFixed(5)}, {lastAlert.longitude.toFixed(5)}
                </p>
                <a
                  href={lastAlert.maps_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--brand-cyan,#FF85AC)] text-sm hover:underline break-all"
                  data-testid="maps-link"
                >
                  {lastAlert.maps_url}
                </a>
              </>
            ) : (
              <p className="text-sm text-[#FF3B30]" data-testid="no-location-warning">
                No se pudo capturar tu ubicación GPS (revisa el permiso de ubicación). Tu grupo fue
                notificado igual, pero descríbeles dónde estás por teléfono si puedes.
              </p>
            )}
          </div>
        )}

        {activeAlertId && !justResolved && (
          <MarkSafeFlow
            alertId={activeAlertId}
            bookingId={user?.booking_id}
            groupId={group?.id}
            senderName={myName}
            onResolved={onMarkedSafe}
          />
        )}

        {group && (
          <div className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-neutral-500" data-testid="protected-banner">
            <Users className="w-3.5 h-3.5 text-[var(--brand-cyan,#FF85AC)]" />
            Grupo protegido: <span className="text-white">{group.name}</span>
          </div>
        )}
      </section>
    </Layout>
  );
}
